import { useCallback } from "react";
import { useSaveBreakRulesConfig, useSaveHoursRateConfig, useSaveOvertimeConfig } from "../../../../../components/team/WageSettings/useWageRulesConfig";
import type { TranslationKeys } from "../../../../../i18n/types";
import {
	requireTransformedRows,
	transformBreakRulesConfigsForBackend,
	transformHoursRateConfigsForBackend,
	transformOvertimeConfigsForBackend,
} from "../../../../../utils/wageRules/forBackend";
import { getBreakRulesConfigReasons, getHoursRateConfigReasons, getOvertimeConfigReasons } from "../../../../../utils/wageRules/validations";
import type { GeneralFormData } from "../types";
import type { AcceptedRow } from "./airtableImportMerge";

/**
 * The three wage rule tables the intake can fill. DAT-905 moved them to Team → Wage Settings, and the
 * department config PUT no longer writes them (`buildDeptConfigUpdatePayload` destructures them out), so
 * an accepted row for one of them would merge into the form and then be dropped by the save in silence.
 * They are saved through the same per-table endpoints Wage Settings uses, once the config save has landed.
 */
const WAGE_RULE_FORM_KEYS = ["hoursRateConfig", "overtimeConfig", "breakRulesConfig"] as const satisfies readonly (keyof GeneralFormData)[];

export type WageRuleFormKey = (typeof WAGE_RULE_FORM_KEYS)[number];

/** The wage rule tables at least one accepted row writes to, in a fixed order. */
export function acceptedWageRuleKeys(accepted: AcceptedRow[]): WageRuleFormKey[] {
	const acceptedKeys = new Set<keyof GeneralFormData>(accepted.map(({ formKey }) => formKey));
	return WAGE_RULE_FORM_KEYS.filter((key) => acceptedKeys.has(key));
}

/**
 * Why the merged rows of these tables cannot be saved, as the same i18n keys Wage Settings shows in its
 * Save tooltip. Checked BEFORE anything is written: the Settings form stopped validating these tables
 * with DAT-905, and the `forBackend` transforms throw on an invalid row rather than explain it.
 */
export function wageRuleReasons(merged: GeneralFormData, keys: WageRuleFormKey[]): TranslationKeys[] {
	return keys.flatMap((key) => WAGE_RULE_REASONS[key](merged));
}

const WAGE_RULE_REASONS: Record<WageRuleFormKey, (merged: GeneralFormData) => TranslationKeys[]> = {
	hoursRateConfig: (merged) => getHoursRateConfigReasons(merged.hoursRateConfig),
	overtimeConfig: (merged) => getOvertimeConfigReasons(merged.overtimeConfig, merged.roles),
	breakRulesConfig: (merged) => getBreakRulesConfigReasons(merged.breakRulesConfig, merged.roles),
};

/**
 * Saves the merged rows of each given table through its Wage Settings endpoint, and resolves to the tables
 * that did NOT save. A failure is never swallowed: each save hook's own `onError` already tracked it and
 * showed the backend's message in a snackbar, and the caller tells the operator which tables are missing.
 */
export function useSaveAcceptedWageRules(): (merged: GeneralFormData, keys: WageRuleFormKey[]) => Promise<WageRuleFormKey[]> {
	const { mutateAsync: saveHoursRate } = useSaveHoursRateConfig();
	const { mutateAsync: saveOvertime } = useSaveOvertimeConfig();
	const { mutateAsync: saveBreakRules } = useSaveBreakRulesConfig();

	return useCallback(
		async (merged, keys) => {
			const { roles, jobIds } = merged;
			const saveOne = (key: WageRuleFormKey) => {
				switch (key) {
					case "hoursRateConfig":
						return saveHoursRate(transformHoursRateConfigsForBackend(merged.hoursRateConfig, roles, jobIds));
					case "overtimeConfig":
						return saveOvertime(requireTransformedRows(transformOvertimeConfigsForBackend(merged.overtimeConfig, roles, jobIds)));
					case "breakRulesConfig":
						return saveBreakRules(requireTransformedRows(transformBreakRulesConfigsForBackend(merged.breakRulesConfig, roles, jobIds)));
				}
			};
			// Independent tables with independent endpoints: one failing must not stop the others landing.
			// `async` so a transform that throws becomes a rejection here rather than escaping `allSettled`.
			const results = await Promise.allSettled(keys.map(async (key) => saveOne(key)));
			return keys.filter((_, index) => results[index].status === "rejected");
		},
		[saveHoursRate, saveOvertime, saveBreakRules],
	);
}
